// =====================================================================
// Phase 11 — Global Duplicate Prevention Service (PURE).
// =====================================================================
//
// Single, contractual entry point that EVERY graph-mutation pathway
// must consult before creating a new family_graph_members row, before
// accepting an invite, before binding identity, before linking a
// spouse, and before linking a parent. Wraps the existing pure
// `findDuplicateMatches` engine (Phase 7D) with explicit contextual
// inputs for each call site so the engine can include the appropriate
// structural anchors (parent / spouse / sibling / claimed-email).
//
// USAGE CONTRACT — codified per the Phase 11 lockdown:
//
//   pre-create person:        runDuplicatePreflight({ source: 'add-graph-member', ... })
//   pre-quick-add:            runDuplicatePreflight({ source: 'quick-add', ... })
//   pre-invite-accept:        runDuplicatePreflight({ source: 'invite-acceptance', ... })
//   pre-identity-bind:        runDuplicatePreflight({ source: 'set-relationship', ... })
//   pre-relationship-link:    runDuplicatePreflight({ source: 'onboarding-relative', ... })
//   pre-spouse-link:          runDuplicatePreflight({ source: 'set-relationship', anchorId: spouseId, ... })
//   pre-parent-link:          runDuplicatePreflight({ source: 'set-relationship', anchorId: parentId, ... })
//
// HARD RULES:
//
//   • Pure read-only delegation — never mutates, never calls Supabase,
//     never overwrites. Compatible with the existing
//     `findDuplicateMatches` semantics; if the master
//     `duplicateGuardrail` flag is OFF the result is `{ blocked: false,
//     matches: [] }` and every mutation pathway runs unchanged.
//   • The threshold map below codifies the Phase 11 contract: STRONG
//     matches BLOCK creation (caller must show the matches and get
//     explicit confirmation); LIKELY matches WARN (caller MUST surface
//     a confirm modal but proceed if user confirms); POSSIBLE matches
//     are advisory only.
//   • The threshold map can be overridden per-call via `policy` — for
//     example, invite acceptance may want to BLOCK on LIKELY too
//     because it implies a new auth user is about to claim a row that
//     may already represent a different real person.
//
// LOCKED-SUBSYSTEM PROMISE: this module does not modify any locked
// file (STABILITY.md §1). It is a thin call-site wrapper around the
// already-shipped `findDuplicateMatches` engine. Existing call sites
// (AddGraphMemberModal etc.) continue to work unchanged; new code
// (and any locked surface that is later promoted out of lock for an
// explicit reason) routes through this single entry point.
// =====================================================================

import type { GraphMember, Relationship, Side } from '@/contexts/FamilyGraphContext';
import {
  findDuplicateMatchesAlways,
  recordDuplicateEvent,
  type DuplicateConfidence,
  type DuplicateMatch,
  type DuplicateEvent,
} from '@/lib/duplicates/duplicateGuard';

export type DuplicateSource = DuplicateEvent['source'];

export type DuplicatePolicyAction = 'allow' | 'warn' | 'block';

export interface DuplicatePolicy {
  /** Confidence levels at or above this trigger a BLOCK. */
  blockAtOrAbove?: DuplicateConfidence | 'never';
  /** Confidence levels at or above this trigger a WARN (must confirm). */
  warnAtOrAbove?: DuplicateConfidence | 'never';
}

const DEFAULT_POLICY: Required<DuplicatePolicy> = {
  // STRONG = block, LIKELY = warn, POSSIBLE = advisory.
  blockAtOrAbove: 'strong',
  warnAtOrAbove: 'likely',
};

/** Stricter policy used for invite acceptance + identity binding. */
const STRICT_POLICY: Required<DuplicatePolicy> = {
  blockAtOrAbove: 'likely',
  warnAtOrAbove: 'possible',
};

const POLICY_BY_SOURCE: Partial<Record<DuplicateSource, Required<DuplicatePolicy>>> = {
  'invite-acceptance': STRICT_POLICY,
  // Identity binding goes through `evaluateBindingSafety` which already
  // enforces a STRONG block; we mirror that here so any caller that
  // routes a "set-relationship" through this guard sees the same gate.
  'set-relationship': STRICT_POLICY,
};

const CONFIDENCE_RANK: Record<DuplicateConfidence, number> = {
  strong: 3,
  likely: 2,
  possible: 1,
};

const meetsThreshold = (
  confidence: DuplicateConfidence,
  threshold: DuplicateConfidence | 'never',
): boolean => {
  if (threshold === 'never') return false;
  return CONFIDENCE_RANK[confidence] >= CONFIDENCE_RANK[threshold];
};

export interface DuplicatePreflightInput {
  source: DuplicateSource;
  /** Trimmed name the user typed. */
  name: string;
  birthYear?: number | null;
  birthDate?: string | null;
  relationship?: Relationship | null;
  side?: Side | null;
  /** Primary anchor (parent / spouse / sibling pivot). */
  anchorId?: string | null;
  /** Optional second anchor (dual-parent link). */
  secondAnchorId?: string | null;
  /** Email hint, if known (e.g. invite acceptance email). */
  email?: string | null;
  /** Currently loaded members. */
  members: GraphMember[];
  /** Optional per-call policy override. */
  policy?: DuplicatePolicy;
}

export interface DuplicatePreflightResult {
  /** `'allow'` = proceed silently. `'warn'` = caller MUST show confirm modal. `'block'` = caller MUST NOT proceed without explicit override. */
  action: DuplicatePolicyAction;
  matches: DuplicateMatch[];
  /** Highest confidence level present in `matches` (or null if none). */
  highest: DuplicateConfidence | null;
  /** Convenience: caller-friendly reason string. */
  reason: string;
}

/**
 * Run the unified pre-mutation duplicate scan. EVERY pathway that
 * creates or links a person should call this first.
 *
 * Returns `'allow'` immediately when no matches exist or when the
 * `duplicateGuardrail` flag is OFF, preserving the locked surface's
 * existing zero-friction behavior.
 */
export function runDuplicatePreflight(
  input: DuplicatePreflightInput,
): DuplicatePreflightResult {
  const matches = findDuplicateMatchesAlways({
    name: input.name,
    birthYear: input.birthYear ?? null,
    birthDate: input.birthDate ?? null,
    relationship: input.relationship ?? null,
    side: input.side ?? null,
    anchorId: input.anchorId ?? null,
    secondAnchorId: input.secondAnchorId ?? null,
    email: input.email ?? null,
    members: input.members,
  });

  if (matches.length === 0) {
    return { action: 'allow', matches: [], highest: null, reason: '' };
  }

  const policy: Required<DuplicatePolicy> = {
    ...DEFAULT_POLICY,
    ...(POLICY_BY_SOURCE[input.source] ?? {}),
    ...(input.policy ?? {}),
  };

  const highest = matches[0]?.confidence ?? null;

  if (highest && meetsThreshold(highest, policy.blockAtOrAbove)) {
    return {
      action: 'block',
      matches,
      highest,
      reason:
        'Likely duplicate — pick the existing person or explicitly confirm a brand-new row.',
    };
  }
  if (highest && meetsThreshold(highest, policy.warnAtOrAbove)) {
    return {
      action: 'warn',
      matches,
      highest,
      reason:
        'A similar person already exists — please confirm before saving.',
    };
  }
  return {
    action: 'allow',
    matches,
    highest,
    reason: '',
  };
}

/**
 * Convenience helper: log the user's resolution of a preflight to the
 * existing duplicate-event ring buffer (PII-minimal). Always safe to
 * call; ignores when guardrail is OFF.
 */
export function logDuplicatePreflightOutcome(
  source: DuplicateSource,
  attemptedName: string,
  match: DuplicateMatch | null,
  action: DuplicateEvent['action'],
  relationship: Relationship | null = null,
): void {
  recordDuplicateEvent({
    attemptedName: attemptedName.slice(0, 80),
    matchedName: match?.member?.name ?? null,
    matchedMemberId: match?.member?.id ?? null,
    confidence: match?.confidence ?? null,
    action,
    source,
    relationship,
  });
}
